import { inject } from '@angular/core';
import { catchError, of, tap, switchMap } from 'rxjs';
import { AuthApi } from '../apis';
import { AuthStore } from '../stores';
import { Storage, MerchantService, AccountService } from '../services';

/**
 * Auth initializer — restore a session on app bootstrap.
 *
 * Trades the persisted refresh token for a fresh access token and rehydrates the rest of
 * the session from localStorage. Fails silently so public routes (the landing page, login)
 * still work when there is nothing to restore.
 *
 * Note this restores an *already scoped* session: the persisted `merchantId` is whichever
 * merchant the user completed login against, so a multi-merchant user comes back to the
 * same one rather than to their primary.
 *
 * Security:
 * - Runs once, on initialization
 * - Clears partial state rather than half-restoring it
 * - Never touches the pending-login credential — that is memory-only by design, so a
 *   reload correctly lands a mid-selection user back on `/login`
 */
export function initializeAuth() {
  const authApi = inject(AuthApi);
  const authStore = inject(AuthStore);
  const storage = inject(Storage);
  const merchantService = inject(MerchantService);
  const accountService = inject(AccountService);

  return () => {
    const refreshToken = storage.getRefreshToken();
    const userData = storage.getUserData();

    if (!refreshToken || !userData) {
      // Clear incomplete session data
      storage.clearRefreshToken();
      storage.clearUserData();
      return of(null);
    }

    return authApi.refresh(refreshToken).pipe(
      tap((response) => {
        authStore.setSession({
          accessToken: response.data.access_token,
          expiresInSeconds: response.data.expires_in,
          user: userData.user,
          countryDetail: userData.countryDetail,
          token: userData.token,
          merchantId: userData.merchantId,
          merchantUserId: userData.merchantUserId,
        });

        // Raw rows go in; `initializeAccounts` normalizes and re-applies the persisted
        // selection (falling back to the default when that id no longer resolves).
        accountService.initializeAccounts(userData.accounts);
      }),
      switchMap(() => {
        const activeAccount = accountService.getActiveAccount();

        if (!activeAccount) {
          console.warn('⚠️ No active account found, skipping merchant details load');
          return of(null);
        }

        return merchantService.loadMerchantDetails(activeAccount.accountId).pipe(
          catchError((error) => {
            console.warn('⚠️ Failed to load merchant details:', error);
            // Don't fail initialization if merchant details fail
            return of(null);
          }),
        );
      }),
      catchError(() => {
        // Clear invalid tokens and user data
        storage.clearRefreshToken();
        storage.clearUserData();
        authStore.clearSession();
        accountService.clearAccounts();
        // Don't throw - allow app to start without authentication
        return of(null);
      }),
    );
  };
}
